// LunaBanner — dismissible top-of-section banner with the luna gradient wash.
// design ref: styles.css:1933 (.luna-banner / .luna-banner__title /
// .luna-banner__body / .luna-banner__close) and bulk-request.jsx:586.
//
// Wider/flatter than LunaCard and uses the slightly stronger `gradientSubtle`
// wash over neutral.0. Renders a 24px close button in the top-right when
// `onDismiss` is supplied.
import { Box, Flex, IconButton, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbX } from "react-icons/tb";

import { LunaMark } from "./LunaMark";

export interface LunaBannerProps {
  /** Title row label. Defaults to "Luna Insight". */
  title?: ReactNode;
  /** Body copy. Constrained to ~720px max width to match `.luna-banner__body`. */
  children: ReactNode;
  /** When supplied, renders a close button in the top-right corner. */
  onDismiss?: () => void;
}

const LunaBanner = ({
  title = "Luna Insight",
  children,
  onDismiss,
}: LunaBannerProps) => {
  return (
    <Box
      position="relative"
      display="flex"
      gap="md" // 12px
      px="lg" // 16px
      py="14px" // .luna-banner padding is 14px 16px (no spacing token for 14)
      borderRadius="component.2xl" // 12px
      borderWidth="1px"
      borderStyle="solid"
      borderColor="p.200"
      // Slightly stronger luna wash than the card — see styles.css:1939–1941.
      bg="linear-gradient(94deg, rgba(252,253,253,0.40) 0%, rgba(237,111,111,0.10) 65%, rgba(113,122,247,0.14) 100%), var(--chakra-colors-neutral-0)"
    >
      {/* Standalone 20px LunaMark on the left — matches bulk-request.jsx:590. */}
      <Box pt="1px">
        <LunaMark size={20} />
      </Box>

      <Flex direction="column" minW={0} flex={1}>
        <Text
          as="span"
          fontFamily="heading"
          fontWeight={600}
          fontSize="md" // 14px — .luna-banner__title
          color="neutral.950"
          mb="2px"
        >
          {title}
        </Text>
        <Box
          fontSize="smd" // 13px
          color="neutral.800"
          lineHeight="1.55"
          maxW="720px"
        >
          {children}
        </Box>
      </Flex>

      {onDismiss && (
        <IconButton
          aria-label="Dismiss"
          icon={<TbX size={14} />}
          onClick={onDismiss}
          position="absolute"
          top="10px"
          right="12px"
          w="24px"
          h="24px"
          minW="24px"
          borderRadius="component.md" // 6px
          color="neutral.600"
          bg="transparent"
          _hover={{ bg: "neutral.100" }}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        />
      )}
    </Box>
  );
};

export default LunaBanner;
